import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';

import type { RoutePlan, RouteTravelLeg } from '@/types/route-plan';
import { NotificationPreferencesManager } from '@/lib/services/NotificationPreferencesManager';

const IS_NATIVE = Platform.OS !== 'web';
const IDENTIFIER_PREFIX = 'route-plan-';
const LEAD_MINUTES = 10;

/**
 * RouteNotificationScheduler schedules one local notification per travel leg
 * (10 minutes before departure) for a route plan. Any edit does a full
 * reschedule; checking off a leg cancels its notification; re-plan or
 * completion cancels everything remaining.
 *
 * Scheduling is skipped entirely (returns 0) when notification permission is
 * denied or app-wide notifications are off — the plan itself still works.
 */
export class RouteNotificationScheduler {
  private buildIdentifier(planId: string, legId: string): string {
    return `${IDENTIFIER_PREFIX}${planId}-${legId}`;
  }

  private async canSchedule(): Promise<boolean> {
    if (!IS_NATIVE) return false;

    const permissions = await Notifications.getPermissionsAsync();
    if (!permissions.granted) return false;

    const preferencesManager = NotificationPreferencesManager.getInstance();
    await preferencesManager.initialize();
    return preferencesManager.getPreferences().notificationsEnabled;
  }

  /**
   * Cancel all route-plan notifications, then schedule one per travel leg
   * whose departure is still in the future. Returns the number scheduled.
   */
  async schedulePlanNotifications(plan: RoutePlan): Promise<number> {
    if (!IS_NATIVE) return 0;

    await this.cancelAll();

    if (!(await this.canSchedule())) return 0;

    const completedSet = new Set(plan.completedLegIds);
    const travelLegs = plan.legs.filter(
      (leg): leg is RouteTravelLeg => leg.kind === 'travel' && !completedSet.has(leg.id),
    );

    const now = new Date();
    let scheduledCount = 0;

    for (const leg of travelLegs) {
      const triggerDate = this.buildTriggerDate(plan.date, leg.departTime);
      if (!triggerDate || triggerDate <= now) continue;

      const nextStopTitle = this.findNextStopTitle(plan, leg);
      await Notifications.scheduleNotificationAsync({
        identifier: this.buildIdentifier(plan.id, leg.id),
        content: {
          title: 'Time to leave soon',
          body: nextStopTitle
            ? `Leave at ${leg.departTime} — next: ${nextStopTitle}`
            : `Leave at ${leg.departTime}`,
          sound: 'default',
          data: { planId: plan.id, legId: leg.id },
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.DATE,
          date: triggerDate,
        },
      });
      scheduledCount++;
    }

    return scheduledCount;
  }

  /**
   * Cancel the notification for a single leg (called on check-off/skip).
   */
  async cancelLegNotification(planId: string, legId: string): Promise<void> {
    if (!IS_NATIVE) return;
    await Notifications.cancelScheduledNotificationAsync(
      this.buildIdentifier(planId, legId),
    ).catch(() => {
      // Already fired or never scheduled — nothing to cancel.
    });
  }

  /**
   * Cancel every scheduled route-plan notification (re-plan, replace, done).
   */
  async cancelAll(): Promise<void> {
    if (!IS_NATIVE) return;
    const scheduled = await Notifications.getAllScheduledNotificationsAsync();
    await Promise.all(
      scheduled
        .filter((notification) => notification.identifier.startsWith(IDENTIFIER_PREFIX))
        .map((notification) =>
          Notifications.cancelScheduledNotificationAsync(notification.identifier),
        ),
    );
  }

  /**
   * Build the local Date for LEAD_MINUTES before departTime on the plan date.
   */
  private buildTriggerDate(date: string, departTime: string): Date | null {
    const [hours, minutes] = departTime.split(':').map((segment) => parseInt(segment, 10));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

    const trigger = new Date(`${date}T00:00:00`);
    if (Number.isNaN(trigger.getTime())) return null;
    trigger.setHours(hours, minutes - LEAD_MINUTES, 0, 0);
    return trigger;
  }

  private findNextStopTitle(plan: RoutePlan, travelLeg: RouteTravelLeg): string | null {
    const index = plan.legs.findIndex((leg) => leg.id === travelLeg.id);
    const nextLeg = index >= 0 ? plan.legs[index + 1] : undefined;
    if (!nextLeg || nextLeg.kind !== 'stay') return null;

    const stop = plan.stops.find((planStop) => planStop.id === nextLeg.stopId);
    return stop?.title ?? null;
  }
}

export const routeNotificationScheduler = new RouteNotificationScheduler();
